// Ported verbatim from nenda-autoplayer's src/types/Schedule.ts. Zero
// framework imports upstream - this IS the legacy wire protocol shape that
// Foyer's own server/services/legacySignageSchedule.ts (BE-13) already
// produces server-side; kept here as the type the ported state machine's
// own actors (fetchSchedule etc.) are written against, so the cast at
// src/tv/signage/foyerScheduleClient.ts's one boundary has something real to
// cast TO.
/**
 * Represents the duration of a media asset in seconds.
 */
export type DurationInSeconds = number;

/**
 * Enum representing different schedule types.
 */
export enum ScheduleType {
  Signage = "Signage",
  Ad = "Ad",
  Content = "Content",
}

/**
 * Enum representing different media types.
 */
export enum MediaType {
  Video = "video",
  Image = "image",
  Slide = "slide",
  URL = "URL",
  LinearChannel = "linearchannel",
}

/**
 * Enum representing different media sources.
 */
export enum MediaSource {
  Redbee = "RedBee",
  Nenda = "Nenda",
  External = "External",
}

export enum OverlayPosition {
  TOP_LEFT = "top-left",
  TOP_RIGHT = "top-right",
  BOTTOM_LEFT = "bottom-left",
  BOTTOM_RIGHT = "bottom-right",
}

export enum ScheduledCommand {
  REBOOT = "reboot",
  LOGOUT = "logout",
}

/**
 * Interface representing a single schedule item.
 */
export interface ScheduleItem {
  id: string;
  name: string;
  playlist: string;
  type: ScheduleType;
}

/**
 * Interface representing an individual media asset.
 */
export interface MediaAsset {
  id: string;
  locator: string;
  dimensions?: { width: number; height: number };
  name: string;
  type: MediaType;
  source: MediaSource;
  durationInSeconds: DurationInSeconds;
}

/**
 * Interface representing ad permissions for different contexts.
 */
export interface AdPermissions {
  external: AdPermission;
  signage: AdPermission;
}

/**
 * Interface representing ad permissions.
 */
export interface AdPermission {
  fullTakeoverAllowed: boolean;
  bannerAllowed: boolean;
}

/**
 * Interface representing options or settings.
 */
export interface Options {
  isSoundEnabled: boolean;
}

/**
 * Interface representing an image overlay.
 */
export interface ImageOverlay {
  locator: string;
  position: OverlayPosition;
}

/**
 * Base interface for content playlists.
 */
export interface ContentPlaylist {
  id: string;
  name: string;
  items: MediaAsset[];
  adPermissions: AdPermissions;
  isSoundEnabled: boolean;
}

/**
 * Interface representing a content channel, extending ContentPlaylist.
 */
export interface ContentChannel extends ContentPlaylist, Overlays {}

export interface Overlays {
  logo?: ImageOverlay;
  brandingOverlay?: ImageOverlay;
}

/**
 * Interface representing a signage playlist, extending ContentPlaylist.
 */
export type SignagePlaylist = ContentPlaylist;

/**
 * Interface representing a playlist.
 *
 * DELTA FROM UPSTREAM: `assets`/`channels`/`signagePlaylist` are optional
 * here, not required. Upstream declares them all as always-present, but the
 * real consuming code (`actions.ts::changeContent`) reads them with
 * `playlist.channels?.length > 0` and `if (playlist.signagePlaylist)` -
 * confirmed by direct read - meaning the type was already inaccurate
 * upstream. Verified load-bearing here: Foyer's own wire producer
 * (`legacySignageSchedule.ts`) emits `{type:"Content", signagePlaylist}` for
 * a normal loop and `{type:"Signage", assets}` for a campaign break -
 * genuinely never all three fields on one entry.
 */
export interface Playlist {
  type: ScheduleType;
  assets?: MediaAsset[];
  channels?: string[];
  signagePlaylist?: string;
}

/**
 * Interface representing the schedule for signage screens.
 */
export interface Schedule {
  channels: ContentChannel[];
  signagePlaylists: SignagePlaylist[];
  contentSchedule: Record<string, ScheduleItem>;
  slotSchedule: Record<string, ScheduleItem>;
  playlists: Record<string, Playlist>;
  options: Options;
  commands: ScheduledCommand[];
}
